import { FileStorageApiClient } from '@omnea-app/packages/universal/fileStorageApiClient/fileStorageApiClient'
import { UploadedFile } from './types'

/**
 * How long a file has from being queued to finishing, not how long its transfer may take — so time spent
 * waiting for a connection counts against it. Deliberately far above what uploads need (p99 is under 7s)
 * because it only decides how quickly an already-doomed request gives up.
 */
export const UPLOAD_DEADLINE_MS = 120_000

type S3UploadFailureCause =
  | { kind: 'network'; message: string }
  | { kind: 'timeout'; timeoutMs: number }
  | { kind: 'http'; status: number }
  | { kind: 'unknown'; message: string }

export type S3UploadFailure = { fileName: string; fileSizeBytes: number } & S3UploadFailureCause

export type UploadFilesResult = {
  uploaded: Required<UploadedFile>[]
  /** Indices into the original `files` array for S3 PUTs that failed */
  failedFileIndices: Set<number>
  /** Classified reason for every entry in `failedFileIndices`, ordered by the original `files` position */
  failures: S3UploadFailure[]
}

type PutOutcome = { status: 'uploaded' } | { status: 'failed'; failure: S3UploadFailure }

const toErrorMessage = (reason: unknown): string => (reason instanceof Error ? reason.message : String(reason))

const putFileToS3 = async (presignedPutUrl: string, file: File, deadline: AbortController): Promise<PutOutcome> => {
  const failed = (cause: S3UploadFailureCause): PutOutcome => ({
    status: 'failed',
    failure: { fileName: file.name, fileSizeBytes: file.size, ...cause },
  })

  if (deadline.signal.aborted) {
    return failed({ kind: 'timeout', timeoutMs: UPLOAD_DEADLINE_MS })
  }

  try {
    const response = await fetch(presignedPutUrl, { method: 'PUT', body: file, signal: deadline.signal })

    // The body is deliberately not read: it is written by whatever answered the PUT, which on a corporate
    // network may be a proxy rather than S3, and no part of it can be trusted or attributed. `status` is the
    // only thing here that came from the transport rather than the payload.
    if (!response.ok) {
      return failed({ kind: 'http', status: response.status })
    }

    return { status: 'uploaded' }
  } catch (error) {
    if (deadline.signal.aborted) {
      return failed({ kind: 'timeout', timeoutMs: UPLOAD_DEADLINE_MS })
    }
    // A bare fetch rejects with TypeError only when the request never completed, which is what a
    // corporate proxy or DLP appliance produces. Browsers disagree on the message, so don't match on it.
    if (error instanceof TypeError) {
      return failed({ kind: 'network', message: toErrorMessage(error) })
    }
    return failed({ kind: 'unknown', message: toErrorMessage(error) })
  }
}

export const uploadFilesToS3 = async (files: File[], authorization: string): Promise<UploadFilesResult> => {
  const fileStorageApiClient = new FileStorageApiClient({ authorization })
  const { data: uploadResponse, error } = await fileStorageApiClient.POST('/upload', {
    body: {
      documents: files.map((doc, index) => ({
        name: doc.name,
        type: doc.type,
        uploadId: index,
      })),
    },
  })

  if (!uploadResponse) {
    throw new Error('Presigned URL fetch failed', { cause: error })
  }

  // Deadlines are armed here, before anything queues, so time a file spends waiting on one of the browser's
  // per-origin connections counts against its own timeout rather than restarting it.
  const queued = uploadResponse.map((doc) => {
    const deadline = new AbortController()
    return { doc, deadline, timeoutId: setTimeout(() => deadline.abort(), UPLOAD_DEADLINE_MS) }
  })

  const putResults = await Promise.allSettled(
    queued.map(async ({ doc, deadline }): Promise<PutOutcome> => {
      const content = files[doc.uploadId]
      if (!content) throw new Error('Document content not found')
      return putFileToS3(doc.presignedPutUrl, content, deadline)
    })
  )

  queued.forEach(({ timeoutId }) => clearTimeout(timeoutId))

  // Index results by the original input position so `uploaded` preserves drop order
  // (the API response may return documents in a different order to the request).
  const resultByUploadId = new Map<number, { doc: (typeof uploadResponse)[number]; result: PromiseSettledResult<PutOutcome> }>()
  putResults.forEach((result, i) => {
    const doc = uploadResponse[i]
    if (doc) resultByUploadId.set(doc.uploadId, { doc, result })
  })

  const uploaded: Required<UploadedFile>[] = []
  const failedFileIndices = new Set<number>()
  const failures: S3UploadFailure[] = []

  const recordFailure = (index: number, failure: S3UploadFailure) => {
    failedFileIndices.add(index)
    failures.push(failure)
  }

  files.forEach((file, i) => {
    const entry = resultByUploadId.get(i)
    if (!entry) {
      recordFailure(i, {
        kind: 'unknown',
        fileName: file.name,
        fileSizeBytes: file.size,
        message: 'No upload target returned for this file',
      })
      return
    }

    if (entry.result.status === 'rejected') {
      recordFailure(i, {
        kind: 'unknown',
        fileName: file.name,
        fileSizeBytes: file.size,
        message: toErrorMessage(entry.result.reason),
      })
      return
    }

    if (entry.result.value.status === 'failed') {
      recordFailure(i, entry.result.value.failure)
      return
    }

    uploaded.push({ key: entry.doc.filestoreId, filename: file.name, contentType: file.type })
  })

  return { uploaded, failedFileIndices, failures }
}
